import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LogIn, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { applyNoIndex } from '../pages/NotFound';

/**
 * Rendered in place of a redirect for auth-gated routes when a guest hits them.
 * Stays mounted, injects a persistent `noindex, nofollow` meta tag, and tells
 * the visitor they need to sign in. This replaces the previous pattern of
 * `<Navigate to="/" />` + `<NoIndex />` which unmounted immediately and
 * stripped the meta tag before any crawler could see it.
 */
function SignInRequired({ onSignIn, title = 'Sign in required' }) {
  useEffect(() => {
    const restore = applyNoIndex();
    const prevTitle = document.title;
    document.title = `${title} | Is It Taxed`;
    return () => {
      restore();
      document.title = prevTitle;
    };
  }, [title]);

  return (
    <div
      className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center px-4 transition-colors"
      data-testid="signin-required-page"
    >
      <div className="max-w-md w-full text-center">
        <div className="inline-block bg-gradient-to-b from-yellow-300 to-yellow-500 px-6 py-3 rounded-lg border-4 border-gray-900 shadow-xl mb-6">
          <LogIn className="h-8 w-8 text-gray-900 mx-auto" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
          Sign in to continue
        </h1>
        <p className="text-base text-gray-600 dark:text-gray-400 mb-8">
          This page is for signed-in members only. Sign in to view your dashboard,
          reports, saved vehicles and reminders.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {onSignIn ? (
            <Button
              className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-semibold w-full sm:w-auto"
              data-testid="signin-required-signin-btn"
              onClick={onSignIn}
            >
              <LogIn className="mr-2 h-4 w-4" />
              Sign in
            </Button>
          ) : (
            <Link to="/">
              <Button
                className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-semibold w-full sm:w-auto"
                data-testid="signin-required-signin-btn"
              >
                <LogIn className="mr-2 h-4 w-4" />
                Sign in
              </Button>
            </Link>
          )}
          <Link to="/">
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              data-testid="signin-required-home-btn"
            >
              <Home className="mr-2 h-4 w-4" />
              Go home
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default SignInRequired;
